import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout, catchError, of } from 'rxjs';

/**
 * Backend health status
 */
export interface HealthStatus {
    isHealthy: boolean;
    message: string;
    checkedAt: Date;
}

/**
 * Service to check backend health status.
 * Uses the /api/health/status endpoint which is anonymous and checks database connectivity.
 */
@Injectable({
    providedIn: 'root'
})
export class HealthService {
    private http = inject(HttpClient);

    /** Current health status as a signal for reactive updates */
    readonly status = signal<HealthStatus>({
        isHealthy: true,
        message: '',
        checkedAt: new Date()
    });

    /** Whether the initial health check has completed */
    readonly initialized = signal<boolean>(false);

    /**
     * Check backend health status.
     * The /api/health/status endpoint returns HTTP 200 with "OK" when healthy,
     * or HTTP 503 when database is unreachable.
     *
     * @param timeoutMs - Request timeout in milliseconds (default: 10000)
     * @returns Promise resolving to health status
     */
    async checkHealth(timeoutMs: number = 10000): Promise<HealthStatus> {
        try {
            // Use /api/health/status endpoint - it's anonymous and checks DB connectivity
            const response = await firstValueFrom(
                this.http.get('/api/health/status', { responseType: 'text' }).pipe(
                    timeout(timeoutMs),
                    catchError(error => {
                        // Network error or timeout - backend is unreachable
                        if (error.status === 0 || error.name === 'TimeoutError') {
                            return of(null);
                        }
                        // HTTP error (500, etc) - backend is up but unhealthy
                        throw error;
                    })
                )
            );

            const healthStatus: HealthStatus = {
                isHealthy: response === 'OK',
                message: response === 'OK' ? '' : (response || 'Estado desconocido'),
                checkedAt: new Date()
            };

            this.status.set(healthStatus);
            this.initialized.set(true);
            return healthStatus;
        } catch (error: any) {
            const healthStatus: HealthStatus = {
                isHealthy: false,
                message: this.getErrorMessage(error),
                checkedAt: new Date()
            };

            this.status.set(healthStatus);
            this.initialized.set(true);
            return healthStatus;
        }
    }

    /**
     * Mark as healthy without checking (for recovery scenarios)
     */
    markHealthy(): void {
        this.status.set({
            isHealthy: true,
            message: '',
            checkedAt: new Date()
        });
    }

    /**
     * Get user-friendly error message based on error type
     */
    private getErrorMessage(error: any): string {
        if (error.status === 0) {
            return 'No se puede conectar con el servidor.';
        }

        if (error.name === 'TimeoutError') {
            return 'El servidor no responde. Por favor, intenta de nuevo.';
        }

        if (error.status >= 500) {
            return error.error || 'El servidor está experimentando problemas.';
        }

        return 'Error al verificar el estado del servidor.';
    }
}
